export const LANGUAGE = {
  EN: 'en',
  AR: 'ar',
};

export const DEFAULT_LANGUAGE = LANGUAGE.AR;
export const SUPPORTED_LANGUAGES = Object.values(LANGUAGE);
export const LANGUAGE_STORAGE_KEY = 'appLanguage';

export const languageOptions = [
  { labelKey: 'WORD_LANGUAGE_ENGLISH', value: LANGUAGE.EN },
  { labelKey: 'WORD_LANGUAGE_ARABIC', value: LANGUAGE.AR },
];

const LANGUAGE_SET = new Set(SUPPORTED_LANGUAGES);

export const getSupportedLanguage = (language) => {
  if (!language || typeof language !== 'string') return null;

  const normalizedLanguage = language.toLowerCase().split('-')[0];
  return LANGUAGE_SET.has(normalizedLanguage) ? normalizedLanguage : null;
};

export const normalizeLanguage = (language) =>
  getSupportedLanguage(language) || DEFAULT_LANGUAGE;

export const getPathLanguage = (pathname = '') => {
  const firstSegment = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return LANGUAGE_SET.has(firstSegment) ? firstSegment : null;
};

export const stripPathLanguage = (path = '') => {
  if (typeof path !== 'string') return path;

  const pathValue = path || '/';
  const language = getPathLanguage(pathValue);
  if (!language) return pathValue;

  const nextPath = pathValue.replace(new RegExp(`^/${language}(?=/|$)`), '');
  return nextPath || '/';
};

export const withPathLanguage = (path, language) => {
  const supportedLanguage = getSupportedLanguage(language);
  if (
    !supportedLanguage ||
    typeof path !== 'string' ||
    !path.startsWith('/') ||
    path.startsWith('//')
  ) {
    return path;
  }

  const cleanPath = stripPathLanguage(path);
  return `/${supportedLanguage}${cleanPath === '/' ? '' : cleanPath}`;
};

export const getLocalizedPath = (path, sourcePathname) => {
  const language = getPathLanguage(sourcePathname);
  return language ? withPathLanguage(path, language) : path;
};

export const getPreferredLanguagePath = (
  path,
  sourcePathname,
  fallbackLanguage
) => {
  const language =
    getPathLanguage(sourcePathname) || getSupportedLanguage(fallbackLanguage);

  return language ? withPathLanguage(path, language) : path;
};

export const getStoredLanguage = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;

  return getSupportedLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
};

export const setStoredLanguage = (language) => {
  const supportedLanguage = getSupportedLanguage(language);
  if (
    !supportedLanguage ||
    typeof window === 'undefined' ||
    !window.localStorage
  ) {
    return;
  }

  localStorage.setItem(LANGUAGE_STORAGE_KEY, supportedLanguage);
};

export const getInitialLanguage = () => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

  return (
    getPathLanguage(window.location.pathname) ||
    getStoredLanguage() ||
    DEFAULT_LANGUAGE
  );
};

export const isRtlLanguage = (language) =>
  normalizeLanguage(language) === LANGUAGE.AR;
